import { IMG, AGENT_AVATAR, DEMO_VIDEO } from "@/lib/images";

export const TEAM_ROUTES = {
  "The Growth Team": "/growth",
  "Workflow & Automation": "/workflow",
};

// The five core platform agents — names, badges, tags, and icons are
// constant across every industry. Descriptions and the receptionist demo
// number are tailored per industry (see each entry below).
export const PLATFORM_AGENTS = [
  {
    key: "sales",
    name: "AI Sales Agent",
    badge: "Close deals 24/7",
    icon: "handshake",
    avatar: AGENT_AVATAR.sales,
    tags: ["Lead conversion", "Multi-channel", "Personalized responses"],
    cta: "watch",
    demoVideoSrc: DEMO_VIDEO.a,
  },
  {
    key: "marketer",
    name: "AI Marketer",
    badge: "Automate campaigns",
    icon: "megaphone",
    avatar: AGENT_AVATAR.marketer,
    tags: ["Personalized campaigns", "Data-driven targeting", "Auto follow-up"],
    cta: "watch",
    demoVideoSrc: DEMO_VIDEO.b,
  },
  {
    key: "reputation",
    name: "AI Reputation Agent",
    badge: "Reputation on autopilot",
    icon: "star",
    avatar: AGENT_AVATAR.reputation,
    tags: ["Auto-responses", "Sentiment analysis", "SEO boost"],
    cta: "watch",
    demoVideoSrc: DEMO_VIDEO.c,
  },
  {
    key: "receptionist",
    name: "AI Receptionist",
    badge: "Never miss a call",
    icon: "phone",
    avatar: AGENT_AVATAR.receptionist,
    tags: ["24/7 availability", "Lead qualification", "Calendar integration"],
    cta: "live",
  },
  {
    key: "scheduler",
    name: "AI Scheduler",
    badge: "Fill your calendar",
    icon: "calendar",
    avatar: AGENT_AVATAR.scheduler,
    tags: ["Smart scheduling", "Reminders", "No-show reduction"],
    cta: "watch",
    demoVideoSrc: DEMO_VIDEO.d,
  },
];

export const INDUSTRIES = {
  "real-estate": {
    route: "/real-estate",
    name: "Real Estate",
    eyebrow: "REAL ESTATE",
    heroImage: IMG.solRealEstate,
    h1: "Every part of how you sell, installed.",
    complianceTag: "Fair Housing–safe.",
    problem:
      "Speed wins listings and leads — but the follow-up, the content, and the reputation work never all happen at once. Pick any two, and the third quietly slips while a faster agent takes the deal.",
    receptionistDemoNumber: "+1 (800) 555-0101",
    platformDescriptions: {
      sales:
        "Turns every property inquiry — portal, ad, or website — into a booked showing, around the clock.",
      marketer:
        "Runs price-drop, new-listing, and past-client campaigns that bring buyers and sellers back.",
      reputation:
        "Replies to every review and pushes your agent profile higher in local search.",
      receptionist:
        "Answers buyer and seller calls 24/7, qualifies them, and books the showing.",
      scheduler:
        "Fills your calendar with showings and listing appointments and cuts no-shows.",
    },
    customAgents: [
      {
        key: "listing-studio",
        name: "The Listing Studio Agent",
        badge: "Listings, camera-ready",
        icon: "clapperboard",
        avatar: AGENT_AVATAR.custom1,
        tags: ["Reels", "Photo copy", "Social & email"],
        cta: "watch",
        outcome:
          "Turns each new listing into a full creative kit — reel, photo copy, social, email.",
        demoVideoSrc: DEMO_VIDEO.b,
      },
      {
        key: "spokesperson",
        name: "The Spokesperson Agent",
        badge: "You on camera, daily",
        icon: "video",
        avatar: AGENT_AVATAR.custom2,
        tags: ["AI avatar", "Daily short-form", "Auto-publish"],
        cta: "watch",
        outcome:
          "A branded AI avatar turns market updates and new listings into daily short-form video, auto-published everywhere.",
        demoVideoSrc: DEMO_VIDEO.c,
      },
      {
        key: "open-house",
        name: "The Open House Funnel Agent",
        badge: "Walk-throughs to pipeline",
        icon: "users",
        avatar: AGENT_AVATAR.custom3,
        tags: ["Landing page", "Creative", "Follow-up"],
        cta: "watch",
        outcome:
          "Builds the registration page, creative, and follow-up funnel that turns walk-throughs into pipeline.",
        demoVideoSrc: DEMO_VIDEO.a,
      },
    ],
    standardsLine:
      "Every public-facing output is cleared against Fair Housing rules by a human before it ships. That's AI Standards.",
    problemSolvers: [
      { problem: "Slow Follow-Up", icon: "handshake", title: "Every lead, worked instantly", body: "An AI sales agent turns every portal, ad, and website inquiry into a booked showing — around the clock, before a faster agent gets there." },
      { problem: "Missed Calls", icon: "phone", title: "Never miss a buyer", body: "An AI receptionist answers buyer and seller calls 24/7, qualifies intent, and books the showing straight to your calendar." },
      { problem: "Cold Database", icon: "megaphone", title: "Bring buyers and sellers back", body: "Price-drop, new-listing, and past-client campaigns run on their own, re-engaging your database while you close." },
      { problem: "Thin Reviews", icon: "star", title: "Reputation that ranks", body: "Every review gets a reply and your agent profile climbs higher in local search — automatically." },
      { problem: "Bland Listings", icon: "clapperboard", title: "Listings, camera-ready", body: "Each new listing becomes a full creative kit — reel, photo copy, social, and email — the moment it goes live." },
      { problem: "No Time to Post", icon: "video", title: "You on camera, daily", body: "A branded AI avatar turns market updates and new listings into daily short-form video, auto-published everywhere." },
    ],
    caseStudy: {
      metric: "3 wks",
      label: "To full deployment across a 12-agent brokerage",
      quote:
        "The follow-up, the listing content, the reviews — it all runs on its own now. I close, the system handles the rest.",
      name: "Alexander W.",
      title: "Founder",
      photo: IMG.clientAlex,
    },
  },

  healthcare: {
    route: "/healthcare",
    name: "Healthcare",
    eyebrow: "HEALTHCARE",
    heroImage: IMG.solHealthcare,
    h1: "Patient growth, handled end to end.",
    complianceTag: "HIPAA-aware; human approval on anything patient-facing.",
    problem:
      "New patients slip through the cracks between the phone, the inbox, and the schedule — and the front desk can't catch every one. The practices that grow are the ones that never miss.",
    receptionistDemoNumber: "+1 (800) 555-0102",
    platformDescriptions: {
      sales:
        "Converts new-patient inquiries from every channel into booked visits.",
      marketer:
        "Runs recare, reactivation, and treatment-plan campaigns that keep chairs full.",
      reputation:
        "Responds to patient reviews and lifts your practice in local search.",
      receptionist:
        "Answers patient calls 24/7, screens them, and books straight to your calendar.",
      scheduler: "Books visits, sends reminders, and slashes no-shows.",
    },
    customAgents: [
      {
        key: "patient-education",
        name: "The Patient Education Agent (Spokesperson)",
        badge: "Educate on autopilot",
        icon: "video",
        avatar: AGENT_AVATAR.custom1,
        tags: ["AI avatar", "Patient education", "Auto-publish"],
        cta: "watch",
        outcome:
          "A provider AI avatar turns common questions and treatments into daily patient-education videos, auto-published.",
        demoVideoSrc: DEMO_VIDEO.c,
      },
      {
        key: "front-desk-ops",
        name: "The Front-Desk Ops Agent",
        badge: "Less paperwork",
        icon: "clipboard",
        avatar: AGENT_AVATAR.custom2,
        tags: ["Forms", "Eligibility", "Admin handoffs"],
        cta: "watch",
        outcome:
          "Handles forms, eligibility checks, and admin handoffs so staff stop drowning in paperwork.",
        demoVideoSrc: DEMO_VIDEO.d,
      },
    ],
    standardsLine:
      "Anything patient-facing pauses for human approval, with HIPAA-aware handling throughout. That's AI Standards.",
    problemSolvers: [
      { problem: "Missed Calls", icon: "phone", title: "Never miss a patient", body: "An AI receptionist answers patient calls 24/7, screens them, and books straight to your calendar — even after hours." },
      { problem: "Empty Chairs", icon: "calendar", title: "Keep the schedule full", body: "An AI scheduler books visits, sends reminders, and slashes no-shows so your providers stay productive." },
      { problem: "Lapsed Patients", icon: "megaphone", title: "Bring patients back", body: "Recare, reactivation, and treatment-plan campaigns run automatically to keep chairs full month after month." },
      { problem: "Poor Reviews", icon: "star", title: "Reputation on autopilot", body: "Every patient review gets a thoughtful, on-brand response that lifts your practice higher in local search." },
      { problem: "Paperwork Overload", icon: "clipboard", title: "Less admin, more care", body: "The Front-Desk Ops Agent handles forms, eligibility checks, and admin handoffs so staff stop drowning in paperwork." },
      { problem: "Uninformed Patients", icon: "video", title: "Educate on autopilot", body: "A provider AI avatar turns common questions and treatments into daily patient-education videos, auto-published." },
    ],
    caseStudy: {
      metric: "+60%",
      label: "New-patient inquiries booked in 90 days",
      quote:
        "We stopped losing patients to voicemail. Every inquiry gets answered and booked, day or night.",
      name: "Darius F.",
      title: "Practice owner",
      photo: IMG.clientDarius,
    },
  },

  legal: {
    route: "/legal",
    name: "Legal",
    eyebrow: "LEGAL",
    heroImage: IMG.solLegal,
    h1: "More signed clients, fully compliant.",
    complianceTag: "Cleared against bar advertising and solicitation rules.",
    problem:
      "Leads arrive at all hours, consults no-show, and every word you publish has to clear the bar's rules. Most firms trade growth for caution — you shouldn't have to.",
    receptionistDemoNumber: "+1 (800) 555-0103",
    platformDescriptions: {
      sales:
        "Turns case inquiries into booked consultations, day or night — compliant.",
      marketer:
        "Runs past-client and referral campaigns that bring new matters in.",
      reputation:
        "Responds to reviews (ethics-compliant) and builds your authority in search.",
      receptionist:
        "Answers prospective-client calls 24/7, screens the matter, and books the consult.",
      scheduler:
        "Books consultations, sends reminders, and reduces no-shows.",
    },
    customAgents: [
      {
        key: "explainer",
        name: "The Explainer Agent (Spokesperson)",
        badge: "Authority on camera",
        icon: "video",
        avatar: AGENT_AVATAR.custom1,
        tags: ["AI avatar", "Compliant explainers", "Auto-publish"],
        cta: "watch",
        outcome:
          "An attorney AI avatar turns common legal questions into compliant short-form explainer videos, auto-published.",
        demoVideoSrc: DEMO_VIDEO.c,
      },
      {
        key: "client-update",
        name: "The Client Update Agent",
        badge: "Fewer status calls",
        icon: "message",
        avatar: AGENT_AVATAR.custom2,
        tags: ["Case updates", "Client comms", "Auto-notify"],
        cta: "watch",
        outcome:
          "Keeps clients informed on case status and cuts \u201cwhat's happening with my case\u201d calls.",
        demoVideoSrc: DEMO_VIDEO.d,
      },
    ],
    standardsLine:
      "Every message and post is checked against bar advertising and solicitation rules by a human before it goes out. That's AI Standards.",
    problemSolvers: [
      { problem: "After-Hours Leads", icon: "handshake", title: "Book consults around the clock", body: "An AI sales agent turns case inquiries into booked consultations, day or night — and stays inside the rules." },
      { problem: "Missed Calls", icon: "phone", title: "Never miss a prospective client", body: "An AI receptionist answers calls 24/7, screens the matter, and books the consult before they call the next firm." },
      { problem: "Consult No-Shows", icon: "calendar", title: "Fewer empty slots", body: "An AI scheduler books consultations, sends reminders, and reduces no-shows so your calendar stays productive." },
      { problem: "Compliance Risk", icon: "star", title: "Reviews, ethics-safe", body: "Every review response respects bar rules while building your authority and ranking in local search." },
      { problem: "Low Authority", icon: "video", title: "Authority on camera", body: "An attorney AI avatar turns common legal questions into compliant short-form explainer videos, auto-published." },
      { problem: "Status-Call Overload", icon: "message", title: "Fewer 'what's happening' calls", body: "The Client Update Agent keeps clients informed on case status automatically, cutting inbound status calls." },
    ],
    caseStudy: {
      metric: "−50%",
      label: "Cut in marketing spend, same signed-client volume",
      quote:
        "We replaced three vendors, stayed inside the rules, and signed more clients than the year before.",
      name: "Ethan P.",
      title: "Managing partner",
      photo: IMG.clientEthan,
    },
  },

  automotive: {
    route: "/automotive",
    name: "Automotive",
    eyebrow: "AUTOMOTIVE",
    heroImage: IMG.solAutomotive,
    h1: "Move more metal, with less manual work.",
    complianceTag: "Accurate inventory and offer claims on everything.",
    problem:
      "Shoppers message five dealers at once. The one who answers first, with the right car, wins — and manual follow-up can't keep that pace across the floor.",
    receptionistDemoNumber: "+1 (800) 555-0104",
    platformDescriptions: {
      sales:
        "Turns inventory inquiries into booked test drives, around the clock.",
      marketer:
        "Runs inventory, promo, and service-reactivation campaigns that move metal.",
      reputation: "Responds to reviews and wins the local search battle.",
      receptionist:
        "Answers sales and service calls 24/7 and books the appointment.",
      scheduler:
        "Books test drives and service appointments and cuts no-shows.",
    },
    customAgents: [
      {
        key: "inventory-spotlight",
        name: "The Inventory Spotlight Agent (Spokesperson)",
        badge: "Inventory in motion",
        icon: "video",
        avatar: AGENT_AVATAR.custom1,
        tags: ["AI avatar", "Daily video", "Auto-publish"],
        cta: "watch",
        outcome:
          "An AI avatar turns new inventory and promos into daily short-form video, auto-published.",
        demoVideoSrc: DEMO_VIDEO.c,
      },
      {
        key: "desk-tools",
        name: "The Desk Tools Agent",
        badge: "A faster floor",
        icon: "wrench",
        avatar: AGENT_AVATAR.custom2,
        tags: ["Internal tools", "CRM ops", "Reporting"],
        cta: "watch",
        outcome:
          "Custom internal tools, CRM ops, and reporting that make the floor faster.",
        demoVideoSrc: DEMO_VIDEO.d,
      },
    ],
    standardsLine:
      "Inventory and offer claims are verified for accuracy by a human before anything publishes. That's AI Standards.",
    problemSolvers: [
      { problem: "Slow Response", icon: "handshake", title: "Answer shoppers first", body: "Shoppers message five dealers at once — an AI sales agent turns inventory inquiries into booked test drives, around the clock." },
      { problem: "Missed Calls", icon: "phone", title: "Never miss a buyer", body: "An AI receptionist answers sales and service calls 24/7 and books the appointment, so no up ever slips away." },
      { problem: "Stale Inventory", icon: "megaphone", title: "Move metal faster", body: "Inventory, promo, and service-reactivation campaigns run automatically to keep your floor and service lane busy." },
      { problem: "Weak Reviews", icon: "star", title: "Win the local search battle", body: "Every review gets a response and your rooftop climbs higher in local search — automatically." },
      { problem: "Static Inventory", icon: "video", title: "Inventory in motion", body: "The Inventory Spotlight Agent turns new inventory and promos into daily short-form video, auto-published." },
      { problem: "Slow Floor", icon: "wrench", title: "A faster floor", body: "The Desk Tools Agent builds custom internal tools, CRM ops, and reporting that speed up every deal." },
    ],
    caseStudy: {
      metric: "+60%",
      label: "Qualified pipeline at a single rooftop",
      quote:
        "We answer every shopper in seconds now, and the floor finally has the tools to close them.",
      name: "Alexander W.",
      title: "General manager",
      photo: IMG.clientAlex,
    },
  },

  "home-services": {
    route: "/home-services",
    name: "Home Services",
    eyebrow: "HOME SERVICES",
    heroImage: IMG.solHomeServices,
    h1: "Booked jobs and a reputation that compounds.",
    complianceTag: "Honest, on-brand claims on every post.",
    problem:
      "The phone rings while you're on a job, quotes go cold, and reviews never get asked for. Booked work leaks out everywhere — and the busiest crews lose the most.",
    receptionistDemoNumber: "+1 (800) 555-0105",
    platformDescriptions: {
      sales:
        "Turns every call, form, and DM into a booked job before a competitor calls back.",
      marketer:
        "Runs seasonal and past-customer campaigns that bring recurring work.",
      reputation: "Requests and responds to reviews to win the local map.",
      receptionist: "Answers calls 24/7, qualifies the job, and books it.",
      scheduler: "Books jobs, sends reminders, and reduces no-shows.",
    },
    customAgents: [
      {
        key: "brand-avatar",
        name: "The Brand Avatar Agent (Spokesperson)",
        badge: "Your brand, daily",
        icon: "video",
        avatar: AGENT_AVATAR.custom1,
        tags: ["AI avatar", "Social video", "Auto-publish"],
        cta: "watch",
        outcome:
          "An AI avatar turns tips, before/afters, and promos into daily social video, auto-published.",
        demoVideoSrc: DEMO_VIDEO.c,
      },
      {
        key: "dispatch",
        name: "The Dispatch Agent",
        badge: "Right tech, right job",
        icon: "route",
        avatar: AGENT_AVATAR.custom2,
        tags: ["Routing", "Field coordination", "Scheduling"],
        cta: "watch",
        outcome:
          "Routes the right tech to the right job and keeps the field team moving.",
        demoVideoSrc: DEMO_VIDEO.d,
      },
    ],
    standardsLine:
      "Every post and claim is checked for honesty and brand fit by a human before it ships. That's AI Standards.",
    problemSolvers: [
      { problem: "Missed Calls", icon: "phone", title: "Never miss a job", body: "The phone rings while you're on a job — an AI receptionist answers calls 24/7, qualifies the work, and books it." },
      { problem: "Cold Quotes", icon: "handshake", title: "Chase every estimate", body: "An AI sales agent turns every call, form, and DM into a booked job before a competitor calls back." },
      { problem: "No-Shows", icon: "calendar", title: "Booked and confirmed", body: "An AI scheduler books jobs, sends reminders, and reduces no-shows so your crews stay fully utilized." },
      { problem: "Few Reviews", icon: "star", title: "Win the local map", body: "Reviews get requested after every job and answered automatically, compounding your reputation over time." },
      { problem: "No Time to Market", icon: "video", title: "Your brand, daily", body: "The Brand Avatar Agent turns tips, before/afters, and promos into daily social video, auto-published." },
      { problem: "Chaotic Scheduling", icon: "route", title: "Right tech, right job", body: "The Dispatch Agent routes the right tech to the right job and keeps the field team moving efficiently." },
    ],
    caseStudy: {
      metric: "3 wks",
      label: "To a full booking + review system, live",
      quote:
        "Calls get answered, estimates get chased, and reviews roll in on their own. We just do the work.",
      name: "Darius F.",
      title: "Owner",
      photo: IMG.clientDarius,
    },
  },
};
